import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, model, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { EditLinks } from '../../../../../@core/constants/edit-links';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { PrimeColumnDef } from '../../../../../@core/types/column-config-types-extended';
import { ColumnConfig, ProductReservedOrderedInfo, ProductReservedOrderedInfoSearchInput, SearchInput } from '../../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../../shared/components/new-base-list-component/new-base-list-component';
import { ProductReservedOrderedInfoService } from '../../../services/product-reserved-ordered-info.service';

export type ProductReservedOrderedFilterType = 'RESERVED' | 'ORDERED' | 'OPEN_REQUESTED';

@Component({
  selector: 'app-widget-product-reserved-ordered-list',
  imports: [
    SharedModule,
    CommonModule,
    BaseListWrapperComponent,
    ButtonModule,
    TooltipModule,
    TranslatePipe
  ],
  templateUrl: './widget-product-reserved-ordered-list.component.html',
  styleUrl: './widget-product-reserved-ordered-list.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductReservedOrderedInfoService]
})
export class WidgetProductReservedOrderedListComponent extends NewBaseListComponent<ProductReservedOrderedInfo | any> {

  searchInputSig = model<ProductReservedOrderedInfoSearchInput>();
  @ViewChild('actionButtonTemplate') actionButtonTemplate!: TemplateRef<{ navigate: () => void; navigateInNewTab: () => void }>;

  constructor(
    private productReservedOrderedInfoService: ProductReservedOrderedInfoService,
    private i18nService: I18nService,
    private router: Router,
    private viewContainerRef: ViewContainerRef
  ) {
    super('ProductReservedOrderedInfo', productReservedOrderedInfoService);
    this.noAddNew = true;
    this.noDelete = true;
    this.noCopy = true;
    this.noBaseEdit = true;
    this.noSearch = true;
    this.noColFilter = true;
    this.tableSelectionMode = null;
    this.showSubmit = false;
  }

  override getSearchInputForQuery(): SearchInput {
    const si = super.getSearchInputForQuery() as ProductReservedOrderedInfoSearchInput;
    const searchInput = this.searchInputSig();
    const filterType = (searchInput?.filterType ?? 'RESERVED') as ProductReservedOrderedFilterType;
    const result: ProductReservedOrderedInfoSearchInput = {
      take: si.take,
      skip: si.skip,
      productIds: searchInput?.productIds ?? si.productIds,
      storagePlaceIds: searchInput?.storagePlaceIds ?? si.storagePlaceIds,
      filterType,
    };
    return result as unknown as SearchInput;
  }

  override async autoLoadColumnConfig(): Promise<void> {

    const filterType = this.searchInputSig().filterType ?? 'RESERVED';
    if (filterType === 'RESERVED') {
      this.viewName.set('Reserved');
    } else if (filterType === 'OPEN_REQUESTED') {
      this.viewName.set('OpenRequested');
    } else {
      this.viewName.set('Ordered');
    }

    return super.autoLoadColumnConfig();
  }

  protected override setColumnConfig(colConfig: ColumnConfig): void {

    let hasActionColumn = false;

    for (let col of colConfig.columns) {
      if (col.field === 'action' && col.dataType === 'custom') {
        hasActionColumn = true;
      }
    }

    if (!hasActionColumn) {
      colConfig.columns.unshift({
        field: 'action',
        dataType: 'custom',
        width: 60,
        deletable: false,
        sortable: false,
        customHeaderName: this.i18nService.translate(_('common.action'))
      });
    }

    super.setColumnConfig(colConfig);
  }

  override getDisplayComponent(outputCell: HTMLElement, outputSpan: HTMLElement, col: PrimeColumnDef, rowData: ProductReservedOrderedInfo | any, row: HTMLTableRowElement) {
    super.getDisplayComponent(outputCell, outputSpan, col, rowData, row);
    if (col.field === 'applicableDate' && rowData.applicableDate) {
      const date = new Date(rowData.applicableDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      if (date < today) {
        outputCell.classList.add('text-red-600');
      }
    }
    if (col.field === 'action' && col.dataType === 'custom' && this.actionButtonTemplate) {
      outputCell.innerHTML = '';
      const view = this.viewContainerRef.createEmbeddedView(this.actionButtonTemplate, {
        navigate: () => this.router.navigate([EditLinks.DOCUMENT + rowData.documentId]),
        navigateInNewTab: () => window.open(EditLinks.DOCUMENT + rowData.documentId, '_blank')
      });
      view.rootNodes.forEach((node) => outputCell.appendChild(node));
      view.detectChanges();
    }
  }

}
